import { FunctionComponent, PropsWithoutRef, forwardRef, Ref, ReactNode } from 'react';
import styled, { css } from 'styled-components';

import {
  Flex,
  Progress,
  useItemIntersection,
  useConsolidatedRef,
  defaultThemeProp,
  ForwardProps,
} from '@pega/cosmos-react-core';
import React from 'react';

export interface RepeatingViewRow {
  id: string | number;
  ariaLabel?: string;
}

export interface RepeatingViewProps {
  /** ref to the list element */
  ref?: Ref<HTMLUListElement>;
  /** Callback to render the view */
  children: (row: RepeatingViewRow) => ReactNode;
  /** Data rows */
  rows?: RepeatingViewRow[];
  /** Indicates if the data is being currently loading */
  loading?: boolean;
  /** Offset of the row item that need to trigger the load more callback */
  offset?: number;
  /** Callback to fetch more rows */
  loadMore?: () => void;
  /** UI that needs to be shown when list is empty */
  emptyView?: ReactNode;
  /** Scroll direction */
  scrollDirection?: 'top-to-bottom' | 'bottom-to-top';
}
export const StyledRepeatingViewItem = styled.li(({ theme }) => {
  return css`
    outline: none;
    &:focus {
      box-shadow: ${theme.base.shadow.focus};
    }
  `;
});
StyledRepeatingViewItem.defaultProps = defaultThemeProp;

export const StyledRepeatingViewUL = styled.ul`
  list-style-type: none;
  overflow-y: auto;
  height: 100%;
  width: 100%;
`;

export const StyledRepeatingView = styled.div`
  position: relative;
`;

export const StyledEmptyView = styled.div`
  position: absolute;
  bottom: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 1;
`;

const RepeatingView: FunctionComponent<RepeatingViewProps & ForwardProps> = forwardRef(
  (props: PropsWithoutRef<RepeatingViewProps>, ref: RepeatingViewProps['ref']) => {
    const {
      children: renderer,
      offset = -1,
      loadMore,
      loading: isLoading = false,
      rows = [],
      emptyView,
      scrollDirection = 'top-to-bottom',
      ...restProps
    } = props;

    const listRef = useConsolidatedRef(ref);
    useItemIntersection(
      listRef,
      offset,
      () => {
        loadMore?.();
      },
      ':scope > li',
    );

    return (
      <Flex
        container={{
          alignItems: 'center',
          justify: 'center',
        }}
        tabindex={0}
        as={StyledRepeatingView}
        {...restProps}
      >
        <Flex
          {...restProps}
          container={{
            direction: scrollDirection === 'top-to-bottom' ? 'column' : 'column-reverse',
          }}
          as={StyledRepeatingViewUL}
          ref={listRef}
        >
          {rows.map((row) => {
            const { ariaLabel, ...rowRestProps } = row;
            return (
              <StyledRepeatingViewItem aria-label={ariaLabel} tabIndex={0} key={row.id}>
                {renderer(rowRestProps)}
              </StyledRepeatingViewItem>
            );
          })}
          {isLoading && (
            <StyledRepeatingViewItem key="loader">
              <Progress placement="inline" />
            </StyledRepeatingViewItem>
          )}
        </Flex>
        {!isLoading && rows.length === 0 && emptyView && <StyledEmptyView>{emptyView}</StyledEmptyView>}
      </Flex>
    );
  },
);

export default RepeatingView;
